import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MissionControl from "@/pages/MissionControl";
import Classroom from "@/pages/Classroom";
import AlmirasDesk from "@/pages/AlmirasDesk";
import ClientSpotlight from "@/pages/ClientSpotlight";
import ReviewPipeline from "@/pages/ReviewPipeline";
import Knowledge from "@/pages/Knowledge";
import Admin from "@/pages/Admin";

function Protected({ children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2E9DB]">
        <div className="text-[#141B4D] font-semibold animate-pulse">Loading Indie University…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function Landing() {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <Navigate to="/login" replace />;
  const fullAccess = user.department === "fulfillment" || user.role === "admin";
  return <Navigate to={fullAccess ? "/bulletin" : "/orientation"} replace />;
}

function AppRoutes() {
  const location = useLocation();
  // Handle OAuth return FIRST (session_id lands in the URL fragment) before any route/auth check.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Landing />} />
      <Route path="/bulletin" element={<Protected><MissionControl /></Protected>} />
      <Route path="/mission-control" element={<Navigate to="/bulletin" replace />} />
      <Route path="/orientation" element={<Protected><Classroom mode="orientation" /></Protected>} />
      <Route path="/trademark-classroom" element={<Protected><Classroom mode="trademark" /></Protected>} />
      <Route path="/classroom" element={<Navigate to="/trademark-classroom" replace />} />
      <Route path="/reply-assistant" element={<Protected><AlmirasDesk initialMode="reply" /></Protected>} />
      <Route path="/guidance-desk" element={<Protected><AlmirasDesk initialMode="guidance" /></Protected>} />
      <Route path="/almiras-desk" element={<Navigate to="/reply-assistant" replace />} />
      <Route path="/client-spotlight" element={<Protected><ClientSpotlight /></Protected>} />
      <Route path="/review" element={<Protected><ReviewPipeline /></Protected>} />
      <Route path="/knowledge" element={<Protected><Knowledge /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
